import {
  type File,
  type Memo,
  type MemoClassificationList,
  type MemoStatusList,
  type UserList,
} from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetTitle,
} from '@kalos/ui';
import { ChevronLeftIcon, ChevronRightIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { type UseQueryResult } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns/esm';
import { type FC, useMemo } from 'react';

import { ROWS_PER_PAGE } from '../../../../constants';
import { useTextNoteTranslationQuery } from '../../../../hooks/react-query/useNoteTranslationQuery';
import { useMobileDevice } from '../../../../hooks/useMedia';
import { OmniGallery } from '../../OmniGallery';
import { memoStatusPillClass } from '../memoTableColumnUtils';
import { MemoEditForm } from './MemoEditForm';

interface MemoDetailSheetProps {
  open: boolean;
  onOpenChange: () => void;
  selectedMemo: Memo;
  memoFiles: File[];
  currentMemoIndex: number;
  sheetCurrentPage: number;
  memoTableLength: number;
  isLoadingNextPage: boolean;
  canNavigateNext: boolean;
  canNavigatePrev: boolean;
  isCanEditTranscript: boolean;
  navigateToMemo: (direction: 'next' | 'prev') => void;
  getFileTypes: (files: File[] | undefined) => {
    hasAudio: boolean;
    hasPhoto: boolean;
    hasVideo: boolean;
  };
  onEditMemo: () => void;
  userBatchGetQuery: UseQueryResult<UserList, Error>;
  memoStatusQuery: UseQueryResult<MemoStatusList, Error>;
  memoClassificationQuery: UseQueryResult<MemoClassificationList, Error>;
  isEditingMemo: boolean;
  setIsEditingMemo: (value: boolean) => void;
  handleSaveMemoInSheet: (updatedMemo?: Memo) => Promise<void>;
  isUpdatingMemo: boolean;
  handleSetState: (partialState: { selectedMemo: Memo }) => void;
  showPagination?: boolean;
  canEditMemoStatus?: boolean;
}

export const MemoDetailSheet: FC<MemoDetailSheetProps> = ({
  open,
  onOpenChange,
  selectedMemo,
  memoFiles,
  currentMemoIndex,
  sheetCurrentPage,
  memoTableLength,
  isLoadingNextPage,
  canNavigateNext,
  canNavigatePrev,
  isCanEditTranscript,
  navigateToMemo,
  getFileTypes,
  onEditMemo,
  userBatchGetQuery,
  memoStatusQuery,
  memoClassificationQuery,
  isEditingMemo,
  setIsEditingMemo,
  handleSaveMemoInSheet,
  isUpdatingMemo,
  handleSetState,
  showPagination = true,
  canEditMemoStatus = true,
}) => {
  const isMobile = useMobileDevice();
  const fileTypes = getFileTypes(selectedMemo.files?.results);
  const attachmentKinds = [
    fileTypes.hasPhoto && 'Photos',
    fileTypes.hasVideo && 'Videos',
    fileTypes.hasAudio && 'Audio',
  ]
    .filter(Boolean)
    .join(', ');
  const textNoteTranslationQuery = useTextNoteTranslationQuery({
    memoId: selectedMemo.id,
    targetLanguage: 'en',
    enabled: open && !!selectedMemo.id && !!selectedMemo.description?.trim(),
  });
  const textNoteTranslation = textNoteTranslationQuery.data ?? null;

  const assignedUser = useMemo(() => {
    if (!selectedMemo.userId) {
      return undefined;
    }
    return userBatchGetQuery.data?.results.find((u) => u.id === selectedMemo.userId);
  }, [selectedMemo.userId, userBatchGetQuery.data?.results]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? 'bottom' : 'modal'}
        // Phones get a labelled Close in the top bar instead of the corner icon.
        closeButtonClassName={isMobile ? 'hidden' : undefined}
        className={
          isMobile
            ? 'max-h-[90vh] overflow-auto rounded-t-lg p-0'
            : 'inset-x-0 inset-y-auto left-0 right-0 top-1/2 mx-auto h-auto max-h-[90vh] w-full max-w-[880px] -translate-y-1/2 overflow-auto pt-10 sm:pt-0 md:p-0'
        }
      >
        <div className="bg-card flex h-full min-h-0 flex-col p-4 sm:p-6">
          <SheetTitle className="sr-only">Note details</SheetTitle>
          {/* Top Navigation Bar */}
          <div className="border-border bg-muted mb-4 rounded-lg border px-3 py-3 sm:px-6 sm:py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {/* Left side - Breadcrumb Navigation */}
              <div className="flex flex-wrap items-center gap-1 text-xs sm:gap-2 sm:text-sm">
                <span className="text-foreground font-medium">Notes</span>
                <ChevronRightIcon className="text-foreground h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-foreground font-medium">
                  Job #{selectedMemo.eventId || 'N/A'}
                </span>
                {showPagination && (
                  <>
                    <ChevronRightIcon className="text-foreground hidden h-4 w-4 sm:block" />
                    <span className="text-muted-foreground hidden font-medium sm:inline">
                      Note {sheetCurrentPage * ROWS_PER_PAGE + currentMemoIndex + 1} of{' '}
                      {memoTableLength}
                    </span>
                    <span className="border-border text-foreground ml-1 rounded-md border border-solid px-2 py-1 sm:ml-2">
                      #{sheetCurrentPage * ROWS_PER_PAGE + currentMemoIndex + 1}
                    </span>
                  </>
                )}
              </div>

              {/* Right side - Prev/Next, then Active Status (margin to avoid close button overlap) */}
              <div className="mr-0 flex flex-wrap items-center gap-2">
                {showPagination && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canNavigatePrev || isLoadingNextPage}
                      onClick={() => navigateToMemo('prev')}
                      className="flex items-center gap-1 text-xs sm:text-sm"
                    >
                      <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Previous</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canNavigateNext || isLoadingNextPage}
                      onClick={() => navigateToMemo('next')}
                      className="flex items-center gap-1 text-xs sm:text-sm"
                    >
                      <span>Next</span>
                      <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </>
                )}
                {isMobile && (
                  <Button size="sm" variant="outline" onClick={onOpenChange}>
                    Close
                  </Button>
                )}
                {/* Active Status - non-interactive */}
                <div
                  className={`flex w-fit items-center gap-1 rounded-md px-2 py-1 text-xs font-medium sm:text-sm ${
                    selectedMemo.isActive
                      ? 'bg-success text-success-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <span>{selectedMemo.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="relative flex-1 overflow-auto">
            {/* Loading overlay */}
            {isLoadingNextPage && (
              <div className="bg-card/80 absolute inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
                <div className="flex flex-col items-center gap-4">
                  <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
                  <p className="text-muted-foreground text-sm">Loading next page...</p>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-4 sm:gap-6">
              {/* Note Details Grid */}
              <div className="border-border bg-muted rounded-lg border p-3 sm:p-6">
                <div className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-2 sm:gap-x-8 sm:gap-y-4 sm:text-sm">
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-0">
                    <span className="text-muted-foreground w-24 shrink-0 font-semibold">
                      Job Number:
                    </span>
                    <span>{selectedMemo.eventId || 'N/A'}</span>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-0">
                    <span className="text-muted-foreground w-24 shrink-0 font-semibold">
                      Submitted:
                    </span>
                    <div>
                      <div className="text-foreground font-normal">
                        {selectedMemo.createdDate
                          ? format(parseISO(selectedMemo.createdDate), 'MMM d, yyyy')
                          : 'N/A'}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {selectedMemo.createdDate
                          ? format(parseISO(selectedMemo.createdDate), 'h:mm a')
                          : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-0">
                    <span className="text-muted-foreground w-24 shrink-0 font-semibold">
                      Property:
                    </span>
                    <span className="text-foreground break-words">
                      {selectedMemo.propertyAddressSearch || 'N/A'}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-0">
                    <span className="text-muted-foreground w-24 shrink-0 font-semibold">
                      Submitter:
                    </span>
                    <span className="break-words">{selectedMemo.createdByName || 'N/A'}</span>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-0">
                    <span className="text-muted-foreground w-24 shrink-0 font-semibold">
                      Department:
                    </span>
                    <span>{selectedMemo.userDepartmentName || 'N/A'}</span>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-0">
                    <span className="text-muted-foreground w-24 shrink-0 font-semibold">
                      Assigned:
                    </span>
                    <span className="text-muted-foreground">
                      {selectedMemo.userId ? (
                        <Badge variant="outline">
                          {assignedUser
                            ? `${assignedUser.firstname} ${assignedUser.lastname}`
                            : 'Unknown User'}
                        </Badge>
                      ) : (
                        <Badge variant="outline">Unassigned</Badge>
                      )}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-0">
                    <span className="text-muted-foreground w-24 shrink-0 font-semibold">
                      Category:
                    </span>
                    <span>
                      {memoClassificationQuery.data?.results.find(
                        (c) => c.id === selectedMemo.classificationId,
                      )?.categoryDescription || 'N/A'}
                    </span>
                  </div>
                  {attachmentKinds && (
                    <div className="flex flex-col gap-1 sm:flex-row sm:gap-0">
                      <span className="text-muted-foreground w-24 shrink-0 font-semibold">
                        Attachments:
                      </span>
                      <span>{attachmentKinds}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Description Section: first on phones, where the note is what you opened. */}
              <div className={isMobile ? 'order-first' : undefined}>
                <div className="mb-2 flex items-center justify-between sm:mb-4">
                  <h3 className="ml-1 text-lg font-normal">Description</h3>
                </div>
                <div className="border-input bg-card rounded-lg border p-3 sm:p-4">
                  <p className="text-foreground text-sm sm:text-base">
                    {selectedMemo.description || 'No description available'}
                  </p>
                  {textNoteTranslation?.translationText &&
                    (!textNoteTranslation.sourceLanguage ||
                      !textNoteTranslation.sourceLanguage.toLowerCase().startsWith('en')) && (
                      <div className="border-border text-muted-foreground mt-2 border-t pt-2 text-xs sm:text-sm">
                        <span className="font-medium">English: </span>
                        <span className="whitespace-pre-wrap">
                          {textNoteTranslation.translationText}
                        </span>
                      </div>
                    )}
                </div>
              </div>

              {/* Administrative Review Section */}
              <div>
                <div className="mb-2 flex flex-col gap-2 sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-lg">Administrative Review:</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex w-fit items-center gap-1 text-xs sm:text-sm"
                    onClick={onEditMemo}
                    disabled={!canEditMemoStatus}
                  >
                    <Pencil1Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                    Admin Review
                  </Button>
                </div>
                <div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium sm:text-sm">Status:</span>
                      <Badge
                        variant="secondary"
                        className={`text-xs sm:text-sm ${memoStatusPillClass(selectedMemo.statusId)}`}
                      >
                        {memoStatusQuery.data?.results.find((s) => s.id === selectedMemo.statusId)
                          ?.statusDescription || 'Pending'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium sm:text-sm">Category:</span>
                      <Badge variant="secondary" className="text-xs sm:text-sm">
                        {memoClassificationQuery.data?.results.find(
                          (c) => c.id === selectedMemo.classificationId,
                        )?.categoryDescription || 'N/A'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium sm:text-sm">Assigned:</span>
                      <Badge variant="secondary" className="text-xs sm:text-sm">
                        {selectedMemo.userId
                          ? assignedUser
                            ? `${assignedUser.firstname} ${assignedUser.lastname}`
                            : 'Unknown User'
                          : 'Unassigned'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gallery Section */}
              <div className="min-h-[250px] flex-1 sm:min-h-[400px]">
                <OmniGallery
                  key={selectedMemo.id} // Force remount when memo changes
                  files={memoFiles}
                  title={selectedMemo.description}
                  showVideos={true}
                  readOnly={!isCanEditTranscript}
                />
              </div>
            </div>
          </div>
        </div>
      </SheetContent>

      {/* Edit Dialog */}
      <Dialog open={isEditingMemo} onOpenChange={(open) => !open && setIsEditingMemo(false)}>
        <DialogContent className="max-h-[90vh] w-[95vw] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Administrative Review
              {showPagination &&
                ` - Note #${sheetCurrentPage * ROWS_PER_PAGE + currentMemoIndex + 1}`}
            </DialogTitle>
          </DialogHeader>
          <MemoEditForm
            memo={selectedMemo}
            onSave={handleSaveMemoInSheet}
            onCancel={() => setIsEditingMemo(false)}
            isLoading={isUpdatingMemo}
            memoStatusOptions={memoStatusQuery.data?.results || []}
            memoClassificationOptions={memoClassificationQuery.data?.results || []}
            onMemoChange={(updatedMemo: Memo) => handleSetState({ selectedMemo: updatedMemo })}
            noteNumber={
              showPagination ? sheetCurrentPage * ROWS_PER_PAGE + currentMemoIndex + 1 : undefined
            }
          />
        </DialogContent>
      </Dialog>
    </Sheet>
  );
};
